
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import type { SavedSignature } from "@/types/signatures";

export const useSignatureDownload = (user: User | null) => {
  const { toast } = useToast();

  const handleDownload = async (signature: SavedSignature, format: 'svg' | 'png', canvasRef?: HTMLCanvasElement) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to download signatures",
        variant: "destructive",
      });
      return;
    }

    try {
      if (format === 'svg') {
        const { data: creditData, error: creditError } = await supabase
          .from('user_credits')
          .select('credits')
          .eq('user_id', user.id)
          .single();

        if (creditError) throw creditError;
        
        if (!creditData || creditData.credits < 2) {
          toast({
            title: "Insufficient Credits",
            description: "You need 2 credits to download an SVG signature",
            variant: "destructive",
          });
          return;
        }

        const { error: deductError } = await supabase
          .from('user_credits')
          .update({ credits: creditData.credits - 2 })
          .eq('user_id', user.id);

        if (deductError) throw deductError;
      }

      await supabase
        .from('signature_downloads')
        .insert({
          user_id: user.id,
          signature_id: signature.id,
          format: format
        });

      if (format === 'svg') {
        const element = document.createElement("a");
        const file = new Blob([signature.svg_content], { type: 'image/svg+xml' });
        element.href = URL.createObjectURL(file);
        element.download = `signature-${signature.text}.svg`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
      } else if (canvasRef) {
        canvasRef.toBlob((blob) => {
          if (!blob) return;
          const element = document.createElement("a");
          element.href = URL.createObjectURL(blob);
          element.download = `signature-${signature.text}.png`;
          document.body.appendChild(element);
          element.click();
          document.body.removeChild(element);
        });
      }

      toast({
        title: "Success",
        description: `Signature downloaded successfully as ${format.toUpperCase()}!`,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Error",
        description: "Failed to process download. Please try again.",
        variant: "destructive",
      });
    }
  };

  return { handleDownload };
};
